import styled, { css } from 'styled-components';

import { SystemColors, Theme } from '../../providers/theme/theme';
import { badgeConsts } from './Badge.consts';
import { BadgeState, BadgeSize, BadgeType } from './Badge.types';

interface BadgeProps {
  size: BadgeSize;
  type: BadgeType;
  state: keyof SystemColors;
  round: boolean;
}

interface BadgeTextProps {
  size: BadgeSize;
}

const getBackgroundColor = (theme: Theme, state: BadgeState, type: BadgeType) => {
  switch (type) {
    case 'subtle':
      return theme.system.subtle[state];
    case 'outlined':
      return 'transparent';
    case 'filled':
    default:
      return theme.system.strong[state];
  }
};

const getColor = (theme: Theme, state: BadgeState, type: BadgeType) => {
  switch (type) {
    case 'subtle':
      return theme.text.onSystem.subtle[state];
    case 'outlined':
      if (state === 'neutral') {
        return theme.text.onSurface.strong;
      }

      return theme.text.onSurface[state];
    case 'filled':
    default:
      return theme.text.onSystem.strong[state];
  }
};

const Badge = styled.div<BadgeProps>`
  ${({ state, type, size, round, theme }) => css`
    width: fit-content;
    min-width: ${badgeConsts[size].size}px;
    min-height: ${badgeConsts[size].size}px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: ${badgeConsts[size].padding};
    border-radius: ${round ? '50%' : '16px'};
    background-color: ${getBackgroundColor(theme, state, type)};
    color: ${getColor(theme, state, type)};
    border: ${type === 'outlined' && `1px solid ${getColor(theme, state, type)}`};

    svg {
      width: ${badgeConsts[size].icon}px;
      height: ${badgeConsts[size].icon}px;

      path {
        fill: ${getColor(theme, state, type)};
      }
    }
  `}
`;

const BadgeText = styled.span<BadgeTextProps>`
  ${({ size, theme }) => css`
    ${size === 'large' && theme.label.large.bold}
    ${size === 'medium' && theme.label.medium.bold}
    ${(size === 'small' || size === 'xsmall') && theme.label.small.bold}
    ${size === 'xsmall' &&
    css`
      line-height: 14px;
    `};
  `};
`;

export { Badge, BadgeText };
